const TURNSTILE_SCRIPT_URL =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"

export type TurnstileInvalidationReason = "error" | "expired" | "timeout"
export type TurnstileAction =
  | "signup"
  | "login"
  | "password_reset"
  | "customer_referral"

export type TurnstileAppearance = "always" | "interaction-only"

export type TurnstileRenderOptions = {
  sitekey: string
  action: TurnstileAction
  theme: "light"
  size: "flexible"
  appearance: TurnstileAppearance
  "response-field": false
  callback: (token: string) => void
  "error-callback": () => void
  "expired-callback": () => void
  "timeout-callback": () => void
}

export type TurnstileApi = {
  render: (target: HTMLElement, options: TurnstileRenderOptions) => string
  remove: (widgetId: string) => void
  reset: (widgetId: string) => void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

// All forms share one page-global Turnstile API. Caching the in-flight promise
// also prevents concurrent mounts from appending duplicate script elements.
let turnstileScriptPromise: Promise<TurnstileApi> | undefined

function isTurnstileApi(value: unknown): value is TurnstileApi {
  return (
    typeof value === "object" &&
    value !== null &&
    "render" in value &&
    typeof value.render === "function" &&
    "remove" in value &&
    typeof value.remove === "function" &&
    "reset" in value &&
    typeof value.reset === "function"
  )
}

export function loadTurnstile(): Promise<TurnstileApi> {
  if (isTurnstileApi(window.turnstile)) {
    return Promise.resolve(window.turnstile)
  }

  if (turnstileScriptPromise) {
    return turnstileScriptPromise
  }

  turnstileScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script")
    script.src = TURNSTILE_SCRIPT_URL
    script.async = true
    script.defer = true

    script.addEventListener("load", () => {
      if (isTurnstileApi(window.turnstile)) {
        resolve(window.turnstile)
        return
      }

      turnstileScriptPromise = undefined
      script.remove()
      reject(new Error("Cloudflare Turnstile loaded without exposing its API"))
    })

    script.addEventListener("error", () => {
      turnstileScriptPromise = undefined
      script.remove()
      reject(new Error("Failed to load Cloudflare Turnstile"))
    })

    document.head.appendChild(script)
  })

  return turnstileScriptPromise
}
